import { BreastTomosynthesisImageStorage } from './Viewer/SOPClasses';
import { Stack } from './ViewportsConfigurations/types';

export const LOADING_PRIORITIES = Object.freeze({
  ACTIVE: 0,
  LINKED: 5,
  HANGED: 20,
  // VX-5244: the step after the active one in a stepped hanging protocol. Each further step adds UPCOMING_STEP_SPACING,
  // up to UPCOMING_STEP_MAX, which every farther step shares.
  UPCOMING_STEP: 200,
  UPCOMING_STEP_MAX: 800,
  UNHANGED: 1000,
  UNHANGED_LOWER_PRIORITY: 10000,
  PREFETCHING: 20000,
});

export type LoadingPriorities = typeof LOADING_PRIORITIES;

export const getUnhungPriority = (stack: Stack): number => {
  if (
    stack &&
    'frames' in stack &&
    stack.frames?.some((frame) => frame?.sopClassUID === BreastTomosynthesisImageStorage)
  ) {
    return LOADING_PRIORITIES.UNHANGED_LOWER_PRIORITY;
  }

  return LOADING_PRIORITIES.UNHANGED;
};

/**
 * VX-5244: spacing between consecutive upcoming steps. The worker adds each frame's distance from the focus slice to
 * the stack priority (`calculateFramePriority`), so the tiers are ordered at the frame level only while that distance
 * stays under the gap. 100 keeps a step's whole stack of up to ~100 slices (a typical tomo) ahead of the next step's.
 *
 * That is also why the cap sits at UPCOMING_STEP_MAX rather than just under UNHANGED: the 200 between them is the
 * headroom that keeps a capped stack of up to ~200 slices ahead of an unhanged stack's focus frame. The near steps get
 * the wide spacing because they are the ones the reader is about to open; steps from the seventh on share one tier.
 */
export const UPCOMING_STEP_SPACING = 100;

/**
 * VX-5244: priority for a stack that is not on screen. `stepDistance` is how many steps ahead of the active one the
 * stack is first shown (1 = the next step), or null when no step shows it. A stack an upcoming step shows takes that
 * step's tier even when it is tomo, which `getUnhungPriority` would otherwise push behind every other stack.
 */
export const getOffscreenPriority = (stack: Stack, stepDistance: Nullable<number>): number => {
  if (stepDistance == null) return getUnhungPriority(stack);

  return Math.min(
    LOADING_PRIORITIES.UPCOMING_STEP + UPCOMING_STEP_SPACING * (stepDistance - 1),
    LOADING_PRIORITIES.UPCOMING_STEP_MAX
  );
};
